import { ReactElement } from "react";

import SPHLogo from "../../assets/SPH24-Logo.svg";
import type { LandingLang } from "./constants";
import { useLandingTranslation } from "./landingLang";

import styles from "./LandingPage.module.scss";

interface LandingFooterProps {
  lang: LandingLang;
}

function EnglishAboutLinks({ t }: { t: (key: string) => string }) {
  return (
    <ul className={styles.footerList}>
      <li>
        <a href={t("footerLinks.promotions")} target="_blank" rel="noreferrer">
          {t("footer.promotions")}
        </a>
      </li>
      <li>
        <a
          href={t("footerLinks.subscriptionTerms")}
          target="_blank"
          rel="noreferrer"
        >
          {t("footer.subscriptionTerms")}
        </a>
      </li>
      <li>
        <a
          href={t("footerLinks.termsConditions")}
          target="_blank"
          rel="noreferrer"
        >
          {t("footer.termsConditions")}
        </a>
      </li>
      <li>
        <a
          href={t("footerLinks.cookieNotice")}
          target="_blank"
          rel="noreferrer"
        >
          {t("footer.cookieNotice")}
        </a>
      </li>
      <li>
        <a
          href={t("footerLinks.personalDataProtection")}
          target="_blank"
          rel="noreferrer"
        >
          {t("footer.personalDataProtection")}
        </a>
      </li>
      <li>
        <a
          href={t("footerLinks.officialDomains")}
          target="_blank"
          rel="noreferrer"
        >
          {t("footer.officialDomains")}
        </a>
      </li>
    </ul>
  );
}

function ChineseAboutLinks({ t }: { t: (key: string) => string }) {
  return (
    <ul className={styles.footerList}>
      <li>
        <a href={t("footerLinks.promotions")} target="_blank" rel="noreferrer">
          {t("footer.promotions")}
        </a>
      </li>
      <li>
        <a
          href={t("footerLinks.subscriptionTerms")}
          target="_blank"
          rel="noreferrer"
        >
          {t("footer.subscriptionTerms")}
        </a>
      </li>
      <li>
        <a
          href={t("footerLinks.termsConditions")}
          target="_blank"
          rel="noreferrer"
        >
          {t("footer.termsConditions")}
        </a>
      </li>
      <li>
        <a
          href={t("footerLinks.cookieNotice")}
          target="_blank"
          rel="noreferrer"
        >
          {t("footer.cookieNotice")}
        </a>
      </li>
      <li>
        <a
          href={t("footerLinks.personalDataProtection")}
          target="_blank"
          rel="noreferrer"
        >
          {t("footer.personalDataProtection")}
        </a>
      </li>
      <li>
        <a
          href={t("footerLinks.officialDomains")}
          target="_blank"
          rel="noreferrer"
        >
          {t("footer.officialDomains")}
        </a>
      </li>
    </ul>
  );
}

function EnglishContact({ t }: { t: (key: string) => string }) {
  return (
    <div>
      <div className={styles.footerContactBlock}>
        <a
          href={t("footerLinks.onlineForm")}
          target="_blank"
          rel="noreferrer"
          className={styles.footerLink}
        >
          {t("footer.onlineForm")}
        </a>
        <p className={styles.footerContactItem}>
          {t("footer.generalEnquiries")}{" "}
          <a href="mailto:circs@sph.com.sg" className={styles.footerLink}>
            circs@sph.com.sg
          </a>
        </p>
        <p className={styles.footerContactItem}>
          {t("footer.corporateEnquiries")}{" "}
          <a href="mailto:corporateCX@sph.com.sg" className={styles.footerLink}>
            corporateCX@sph.com.sg
          </a>
        </p>
      </div>
    </div>
  );
}

function ChineseContact({ t }: { t: (key: string) => string }) {
  return (
    <div>
      <div className={styles.footerContactBlock}>
        <a
          href={t("footerLinks.onlineForm")}
          target="_blank"
          rel="noreferrer"
          className={styles.footerFeedbackLink}
        >
          {t("footer.feedback")}
        </a>
        <p className={styles.footerContactItem}>
          {t("footer.generalEnquiries")}{" "}
          <a href="mailto:circs@sph.com.sg" className={styles.footerLink}>
            circs@sph.com.sg
          </a>
        </p>
      </div>
      <div className={styles.footerContactBlock}>
        <p className={styles.footerContactItem}>
          {t("footer.corporateEnquiries")}{" "}
          <a href="mailto:corporateCX@sph.com.sg" className={styles.footerLink}>
            corporateCX@sph.com.sg
          </a>
        </p>
      </div>
    </div>
  );
}

export default function LandingFooter({
  lang,
}: LandingFooterProps): ReactElement {
  const { t } = useLandingTranslation();
  const year = new Date().getFullYear();

  return (
    <footer className={styles.footer}>
      <div className={styles.footerContent}>
        <div>
          <div className={styles.footerColumns}>
            <div className={styles.footerColumn}>
              <h3 className={styles.footerHeading}>{t("footer.about")}</h3>
              {lang === "zh" ? (
                <ChineseAboutLinks t={t} />
              ) : (
                <EnglishAboutLinks t={t} />
              )}
            </div>
            <div className={styles.footerColumn}>
              <h3 className={styles.footerHeading}>{t("footer.contactUs")}</h3>
              {lang === "zh" ? (
                <ChineseContact t={t} />
              ) : (
                <EnglishContact t={t} />
              )}
            </div>
          </div>
          <div className={styles.footerBottom}>
            <p>{t("footer.copyright", { year })}</p>
            <p>{t("footer.address")}</p>
          </div>
        </div>
        <img src={SPHLogo} alt="SPH Media" className={styles.footerLogo} />
      </div>
    </footer>
  );
}
